import { TextInput } from "@/components/atoms";
import { ChevronDownSvg } from "@/components/atoms/icons";
import { Typography } from "@/components/atoms/Typography";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { COUNTRIES, type Country } from "@/core/utils/countryCodes";
import React, { useRef, useState } from "react";
import {
  FlatList,
  Modal,
  TextInput as RNTextInput,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";

export interface CountryCodeDropdownProps {
  selectedCountry?: Country | undefined;
  onCountrySelect: (country: Country) => void;
  disabled?: boolean | undefined;
  containerStyle?: any;
}

export function CountryCodeDropdown({
  selectedCountry = COUNTRIES[0],
  onCountrySelect,
  disabled = false,
  containerStyle,
}: CountryCodeDropdownProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const [isVisible, setIsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<RNTextInput>(null);

  const filteredCountries = COUNTRIES.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery),
  );

  const handleCountrySelect = (country: Country) => {
    // Blur the search input to close keyboard
    searchInputRef.current?.blur();
    // Select the country
    onCountrySelect(country);
    // Close modal and clear search
    setIsVisible(false);
    setSearchQuery("");
  };

  // Dynamic styles based on selection state - match NationalityDropdown
  const getInputContainerStyle = (): ViewStyle => {
    return {
      flexDirection: isRTL ? "row-reverse" : "row",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderWidth: 1,
      borderRadius: 8,
      backgroundColor: selectedCountry ? colors.backgroundLevel2 : "#f2f2f2",
      borderColor: selectedCountry ? colors.backgroundLevel4 : "transparent",
      height: 48,
      gap: 8,
      minWidth: 110, // Reduced width while still accommodating 3-digit country codes like +971
    };
  };

  const styles = StyleSheet.create({
    container: {
      // Base styles - dynamic styles applied via getInputContainerStyle()
    },
    disabledContainer: {
      opacity: 0.6,
    },
    selectedContent: {
      flexDirection: isRTL ? "row-reverse" : "row",
      alignItems: "center",
      flexShrink: 1, // Allow shrinking but prevent overflow
      gap: 8,
      minWidth: 0, // Prevent flex item from overflowing
    },
    flag: {
      fontSize: 20,
      textAlign: "center",
      lineHeight: 24,
      includeFontPadding: false,
      paddingBottom: 4,
    },
    code: {
      color: colors.fontPrimary,
      fontSize: 16,
      fontWeight: "400",
      fontFamily: "Matter",
      lineHeight: 24,
      includeFontPadding: false,
      minWidth: 30, // Reduced minimum width to accommodate 3-digit codes like "+971"
    },
    chevron: {
      width: 19,
      height: 19,
      transform: [{ scaleX: isRTL ? -1 : 1 }],
      flexShrink: 0, // Prevent chevron from shrinking
    },
    modal: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      justifyContent: "center",
      alignItems: "center",
    },
    modalContent: {
      backgroundColor: colors.backgroundLevel1,
      borderRadius: 12,
      width: "90%",
      maxHeight: "80%",
      paddingVertical: 20,
    },
    modalHeader: {
      paddingHorizontal: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.outlineInactive,
    },
    modalTitle: {
      color: colors.fontPrimary,
      fontSize: 18,
      fontWeight: "600",
      textAlign: "center",
    },
    searchContainer: {
      paddingHorizontal: 20,
      paddingVertical: 16,
    },
    countryList: {
      maxHeight: 300,
    },
    countryItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 20,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.outlineInactive,
    },
    selectedCountryItem: {
      backgroundColor: colors.backgroundLevel3,
    },
    countryFlag: {
      fontSize: 24,
      marginRight: 12,
    },
    countryInfo: {
      flex: 1,
    },
    countryName: {
      color: colors.fontPrimary,
      fontSize: 16,
      fontWeight: "500",
    },
    countryCode: {
      color: colors.fontSecondary,
      fontSize: 14,
      marginTop: 2,
    },
  });

  return (
    <>
      <TouchableOpacity
        style={[
          getInputContainerStyle(),
          disabled && styles.disabledContainer,
          containerStyle,
        ]}
        onPress={() => !disabled && setIsVisible(true)}
        disabled={disabled}
      >
        {/* Chevron first in RTL, last in LTR */}
        {!disabled && isRTL && (
          <View style={styles.chevron}>
            <ChevronDownSvg size={19} color={colors.fontPrimary} />
          </View>
        )}

        {/* Content area with proper RTL ordering */}
        <View style={styles.selectedContent}>
          <View>
            <Text style={styles.flag}>{selectedCountry?.flag}</Text>
          </View>
          <Text style={styles.code}>{selectedCountry?.code}</Text>
        </View>

        {/* Chevron last in LTR, first in RTL */}
        {!disabled && !isRTL && (
          <View style={styles.chevron}>
            <ChevronDownSvg size={19} color={colors.fontPrimary} />
          </View>
        )}
      </TouchableOpacity>

      <Modal
        visible={isVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsVisible(false)}
      >
        <TouchableOpacity
          style={styles.modal}
          activeOpacity={1}
          onPress={() => setIsVisible(false)}
        >
          <TouchableOpacity
            style={styles.modalContent}
            activeOpacity={1}
            onPress={() => {}}
          >
            <View style={styles.modalHeader}>
              <Typography variant="headline" style={styles.modalTitle}>
                {t("profile.selectCountry")}
              </Typography>
            </View>

            <View style={styles.searchContainer}>
              <TextInput
                ref={searchInputRef}
                placeholder={t("profile.searchCountryOrCode")}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCapitalize="none"
                variant="search"
              />
            </View>

            <FlatList
              data={filteredCountries}
              keyExtractor={(item) => item.country}
              style={styles.countryList}
              keyboardShouldPersistTaps="handled" // Allow taps on list items even when keyboard is open
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.countryItem,
                    selectedCountry?.country === item.country &&
                      styles.selectedCountryItem,
                  ]}
                  onPress={() => {
                    // Blur the search input before selecting to ensure clean state
                    handleCountrySelect(item);
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={styles.countryFlag}>{item.flag}</Text>
                  <View style={styles.countryInfo}>
                    <Typography variant="body" style={styles.countryName}>
                      {item.name}
                    </Typography>
                    <Typography variant="footnote" style={styles.countryCode}>
                      {item.code}
                    </Typography>
                  </View>
                </TouchableOpacity>
              )}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
}
